'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Star, Send, CheckCircle2, MessageSquare, Loader2 } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useGame } from '@/context/GameContext';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export interface GameRatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  autoPopped?: boolean;
}

export const GameRatingModal: React.FC<GameRatingModalProps> = ({
  isOpen,
  onClose,
  autoPopped = false
}) => {
  const { fetchWithId, playerId } = useGame();
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setRating(0);
      setComment('');
      setIsSuccess(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (rating === 0) return;
    setIsSubmitting(true);

    try {
      const response = await fetchWithId('/feedback/ratings', {
        method: 'POST',
        body: JSON.stringify({
          playerId,
          rating,
          comment: comment.trim() || undefined
        }),
      });

      if (response.ok) {
        setIsSuccess(true);
        localStorage.setItem('fjalafundit_last_rating_date', new Date().toISOString());
        
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to submit rating:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="relative w-full max-w-md bg-card doodle-border p-6 md:p-10 shadow-doodle-xl overflow-hidden"
      >
        <button
          onClick={onClose}
          type="button"
          className="absolute top-4 right-4 p-2 hover:bg-muted rounded-full transition-colors boing z-50"
        >
          <X className="w-6 h-6 text-slate-400" />
        </button>

        <div className="space-y-8">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 dark:bg-amber-900/20 text-amber-600 rounded-full text-[10px] font-black uppercase tracking-widest handwritten">
              <Star className="w-3 h-3 fill-current" />
              VLERËSIMI
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-pink-500 handwritten leading-none uppercase">
              {autoPopped ? 'A po ju pëlqen loja?' : 'Vlerësoni lojën'}
            </h2>
            <p className="text-sky-500 font-bold handwritten text-sm md:text-lg">
              Ndihmo si të përmirësojmë lojën
            </p>
          </div>

          {isSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-12 flex flex-col items-center justify-center text-center space-y-6"
            >
              <div className="w-24 h-24 bg-emerald-50 doodle-border-soft border-emerald-200 flex items-center justify-center text-emerald-500 shadow-doodle shadow-emerald-100 dark:shadow-none -rotate-3">
                <CheckCircle2 className="w-14 h-14 stroke-[3px]" />
              </div>
              <div className="space-y-2">
                <p className="font-black handwritten text-3xl text-emerald-700 dark:text-emerald-400 uppercase tracking-tight">Faleminderit!</p>
                <p className="text-emerald-600/70 dark:text-emerald-500/70 font-bold handwritten text-lg text-center px-4">Vlerësimi juaj u dërgua me sukses.</p>
              </div>
            </motion.div>
          ) : (
            <div className="space-y-8">
              {/* Star Rating */}
              <div className="flex justify-center gap-1 md:gap-1.5 py-2 overflow-visible">
                {[1, 2, 3, 4, 5].map((star) => (
                  <motion.button
                    key={star}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    onClick={() => setRating(star)}
                    whileHover={{ 
                      scale: 1.15, 
                      rotate: star % 2 === 0 ? 5 : -5,
                      transition: { type: "spring", stiffness: 400, damping: 10 }
                    }}
                    whileTap={{ scale: 0.9 }}
                    className="relative focus:outline-none z-10 will-change-transform"
                  >
                    <Star 
                      className={cn(
                        "w-10 h-11 md:w-14 md:h-16 transition-all duration-150 drop-shadow-sm pointer-events-none",
                        (hoveredRating || rating) >= star 
                          ? "fill-amber-400 text-amber-500" 
                          : "text-slate-200 dark:text-slate-800"
                      )}
                      strokeWidth={2}
                    />
                  </motion.button>
                ))}
              </div>

              {/* Comment Field (Optional) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between px-1">
                  <label className="text-[10px] uppercase font-black text-slate-400 handwritten flex items-center gap-2">
                    <MessageSquare className="w-3 h-3 text-emerald-500" /> Mendime apo sugjerime (Opsionale)
                  </label>
                  <span className={cn(
                    "text-[9px] font-black handwritten tracking-tighter",
                    comment.length >= 450 ? "text-rose-500" : "text-slate-300"
                  )}>
                    {comment.length} / 500
                  </span>
                </div>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={500}
                  rows={3}
                  className="w-full p-4 doodle-border-soft bg-muted/20 font-bold handwritten text-base text-emerald-600 outline-none resize-none focus:bg-card transition-all placeholder:opacity-30"
                  placeholder="Çfarë mund të bëjmë më mirë?"
                />
              </div>


              <button
                onClick={handleSubmit}
                disabled={isSubmitting || rating === 0}
                className={cn(
                  "w-full bg-pink-500 text-white py-4 md:py-5 doodle-border font-black text-xl md:text-2xl transition-all flex items-center justify-center gap-3",
                  (isSubmitting || rating === 0) 
                    ? "opacity-40 cursor-not-allowed grayscale pointer-events-none shadow-none" 
                    : "hover:translate-x-[-2px] hover:translate-y-[-2px] active:scale-95 boing shadow-doodle md:shadow-doodle-lg"
                )}
              >
                {isSubmitting ? (
                  <Loader2 className="w-7 h-7 animate-spin" />
                ) : (
                  <>
                    <Send className="w-6 h-6" />
                    DËRGO VLERËSIMIN
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};
